import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Dropdown, HooksService, WithTooltip } from 'basic-components';
import { Settings } from 'basic-components/icons';
import Query from 'dashboard-services/api/Query';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';
import { FormulaUtils } from 'utils';

import DefaultColumnsListModal from 'primary/DefaultColumnsListModal';

import ContextMenu from './ContextMenu';

import { FocusTrap } from 'focus-trap-react';
import { When } from 'react-if';

const FOCUS_TRAP_CONFIG = {
  clickOutsideDeactivates: false,
  allowOutsideClick: true,
}

const ESC_KEY = 27,
      emptyArray = [],
      emptyFun = () => {}
const ColumnSettings = () => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.localSource),
        selectedSymbols = useSelector(state => state.seriesState.creatorState.selectedSymbols) || emptyArray,
        selectedColumns = useSelector(state => state.seriesState.creatorState.selectedColumns) || emptyArray,
        [isOpen, setIsOpen] = useState(false),
        [isDefaultColumnsListOpen, setIsDefaultColumnsListOpen] = useState(false),
        [isMenuOpen, setIsMenuOpen] = useState(false),
        [isContextMenuOpen, setIsContextMenuOpen] = useState(false),
        swapIsOpen = useCallback(() => setIsOpen(isOpen => {
          if(isOpen) {
            const close = e => {
              if(e.keyCode === ESC_KEY){
                swapIsOpen()
              }
            }
            addEventListener('keydown', close)
          } else {
            removeEventListener('keydown', close)
          }
          return !isOpen;
        }), []),
        targetSource = useMemo(() => {
          if (selectedSymbols?.[0]?.source) {
            return selectedSymbols[0].source
          } else {
            return source
          }
        }, [selectedSymbols, source]),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(getApiConfigLocal())
            .withFetching(false)
            .withHeader("Target", targetSource, true)
        , [getApiConfigLocal, targetSource]),
        { data: columns = emptyArray } = dataStorageQueries.useGetColumns({ getQueryBuilder, selectedSymbols, provider: targetSource }),
        onChange = useCallback((value, { name }) => dispatch(creatorSeriesActions.onChange(value, name)), [dispatch]),
        prevColumns = HooksService.usePrevious(columns),
        selectedColumnsLength = selectedColumns?.length,
        prevSelectedColumnsLength = HooksService.usePrevious(selectedColumnsLength),
        columnsRef = useRef(),
        setDefaultColumns = useCallback(() => {
          columnsRef.current.dropdown.current.setValue([columns[0]])
          swapIsOpen()
        }, [columns, swapIsOpen]),
        openDefaultColumnsList = useCallback(() => setIsDefaultColumnsListOpen(true), []),
        closeDefaultColumnsList = useCallback(() => setIsDefaultColumnsListOpen(false), []),
        onChangeColumns = useCallback(value => dispatch(creatorSeriesActions.onChange(value, "selectedColumns")), [dispatch]),
        applyToAll = useCallback(() => {
          swapIsOpen()
          selectedSymbols.forEach((symbol, index) => {
            if(symbol?.columns && !FormulaUtils.isRawFormulaOverridden(symbol?.formulaExpressions?.columns)) {
              dispatch(creatorSeriesActions.onRemoveSymbolColumns(index))
            }
          })
        }, [dispatch, selectedSymbols, swapIsOpen]),
        timeoutRef = useRef(),
        onMenuOpen = useCallback(() => setIsMenuOpen(true), []),
        onMenuClose = useCallback(() => {
          timeoutRef.current = setTimeout(() => setIsMenuOpen(false), 100)
        }, []),
        handleOnKey = useCallback(e => {
          if ((e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            swapIsOpen()
          }
        }, [swapIsOpen])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
    removeEventListener('keydown', close)
  }, [])

  useEffect(() => {
    if(isOpen) {
      if(selectedColumns.includes("*") && columns?.length > 0) {
        columnsRef.current.dropdown.current.setValue(columns)
      } else if((!prevColumns || prevColumns?.length === 0) && columns[0] && selectedColumns.length === 0 && prevSelectedColumnsLength !== 1) {
        columnsRef.current.dropdown.current.setValue([columns[0]])
      }
    }
  }, [columns, isOpen, onChange, prevColumns, prevSelectedColumnsLength, selectedColumns])

  return (
    <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header data-storage__creator__add-data-panel__content__headers__header--columns">
      <When condition={isOpen}>
        {() => (
          <div 
              className="ng-office-app__authed__content__basket__body__data__basket__headers__header__bg"
              onClick={isMenuOpen ? emptyFun : swapIsOpen}
          />
        )}
      </When>
      <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__text">
        Columns
      </div>
      <WithTooltip
          data-place="left"
          short
          text="Set default column"
      >
        <div
            aria-label="Default columns selection"
            className="ng-office-app__authed__content__basket__body__data__basket__headers__header__icon"
            onClick={swapIsOpen}
            onKeyDown={handleOnKey}
            tabIndex={0}
        >
          <Settings
              color="blue-bright"
              height={12}
              width={12}
          />
        </div>
      </WithTooltip>
      <When condition={isOpen}>
        {() => (
          <FocusTrap 
              active={isOpen}
              focusTrapOptions={FOCUS_TRAP_CONFIG}
              paused={isContextMenuOpen}
          >
            <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings">
              <div 
                  className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__header"
                  id="default-columns"
              >
                <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__header__left">
                  Default Columns
                </div>
                <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__header__right">
                  <ContextMenu
                      openDefaultColumnsList={openDefaultColumnsList}
                      setDefaultColumns={setDefaultColumns}
                      setIsMenuOpen={setIsContextMenuOpen}
                  />
                </div>
              </div>
              <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__dropdown">
                <Dropdown
                    autoFocus
                    defaultValue={selectedColumns}
                    disabled={selectedSymbols.length === 0}
                    labelId="default-columns"
                    menuPlacement="bottom"
                    multi
                    name="selectedColumns"
                    noError
                    onChange={onChange}
                    onMenuClose={onMenuClose}
                    onMenuOpen={onMenuOpen}
                    options={columns}
                    placeholder="Columns"
                    ref={columnsRef}
                    variant="border"
                />
              </div>
              <div className="ng-office-app__authed__content__basket__body__data__basket__headers__header__settings__footer">
                <Button
                    onClick={swapIsOpen}
                    size="small"
                    variant="text"
                >
                  Cancel
                </Button>
                <Button
                    onClick={applyToAll}
                    size="small"
                    variant="secondary"
                >
                  Apply to all
                </Button>
              </div>
            </div>
          </FocusTrap>
        )}
      </When>
      <When condition={isDefaultColumnsListOpen}>
        {() => (
          <DefaultColumnsListModal
              close={closeDefaultColumnsList}
              onChangeColumns={onChangeColumns}
          />
        )}
      </When>
    </div>
  )
}

export default memo(ColumnSettings);
